import { parseMobileSession } from "@fluentry/domain";
import type { MobileSessionPayload } from "@fluentry/platform-adapters";
import { requestJson } from "@fluentry/shared-workbench/http";
import { resolveProductAppApiBaseUrl } from "./auth-client";

function requireMobileSessionPayload(payload: unknown): MobileSessionPayload {
  const session = parseMobileSession(payload);
  if (!session) {
    throw new Error("Mobile exchange response did not include a valid session");
  }

  const expiresAt = new Date(session.expiresAt).getTime();
  if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
    throw new Error("Mobile exchange response included an expired session");
  }

  return session;
}

export async function requestMobileExchangeSession(
  email: string,
  platform: "ios" | "android",
): Promise<MobileSessionPayload> {
  const apiBaseUrl = resolveProductAppApiBaseUrl();
  const payload = await requestJson<
    { success: true; result: MobileSessionPayload } | { success: false; error: { message: string } }
  >("/api/auth/mobile/exchange", {
    apiBaseUrl,
    method: "POST",
    bodyJson: {
      email,
      platform,
    },
  });

  if (!payload.success) {
    throw new Error(payload.error.message);
  }

  // Validate before secure storage persistence so malformed server success cannot create a local auth session.
  return requireMobileSessionPayload(payload.result);
}

export async function revokeMobileExchangeSession(session: MobileSessionPayload): Promise<void> {
  const apiBaseUrl = resolveProductAppApiBaseUrl();
  const payload = await requestJson<
    { success: true; result: { revoked: boolean } } | { success: false; error: { message: string } }
  >("/api/auth/mobile/session", {
    apiBaseUrl,
    method: "DELETE",
    headers: {
      authorization: `Bearer ${session.token}`,
    },
  });

  if (!payload.success) {
    throw new Error(payload.error.message);
  }

  if (!payload.result.revoked) {
    throw new Error("Mobile session was not revoked");
  }
}
